import {
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { useRef } from 'react';
import {
  ENTITY__SCHOOL
} from '../../../../../dictionaries/Entities.dic';
import {
  addElements,
  Arr,
  removeElements,
  sanitizeArr,
  yesAllElementsIncluded
} from '../../../../../lib/Array.lib';
import {
  NOT,
  YES
} from '../../../../../lib/Boolean.lib';
import Core from '../../../../../lib/Core';
import globalErrorHandler from '../../../../../lib/Error/globalErrorHandler.fun';
import {
  replaceRegExp
} from '../../../../../lib/GenericTools.lib';
import useState from '../../../../../lib/hooks/useState.hook';
import {
  Obj
} from '../../../../../lib/Object.lib';
import {
  COLLECTION__COMPANY,
  COLLECTION__SCHOOL,
  readLoopbackRecord
} from '../../../../../lib/services/BE/loopback.api';
import {
  joinKeyName
} from '../../../../../lib/String.lib';
import {
  joinClassName
} from '../../../../Layout/Libraries/Theme.lib';
import Box from '../../../../Layout/Wrappers/Box';
import Button from '../../../../Layout/Wrappers/Button';
import Checkbox from '../../../../Layout/Wrappers/Checkbox';
import Chip from '../../../../Layout/Wrappers/Chip';
import Fieldset from '../../../../Layout/Wrappers/Fieldset';
import Icon from '../../../../Layout/Wrappers/Icon';
import IconButton from '../../../../Layout/Wrappers/IconButton';
import List from '../../../../Layout/Wrappers/List';
import Message from '../../../../Layout/Wrappers/Message';
import Once from '../../../../Layout/Wrappers/Once';
import {
  PLACEMENT__TOP_END
} from '../../../../Layout/Wrappers/StyledTooltip';
import TextField from '../../../../Layout/Wrappers/TextField';
import Typography from '../../../../Layout/Wrappers/Typography';
import {
  getControllerJobEdit
} from '../../JobEdit';
import {
  getControllerSectionTargetAvoidPreferences
} from './SectionTargetAvoidPreferences';

const LIMIT__FETCH_DATA_FOR_UNDEFINED_CASE = 200;
const DELAY__FETCH_DATA_AFTER_TYPE = 500;

export const KEY__CONTROLLER__EDIT_TARGET_AVOID_PREFERENCES = 'editTargetAvoidPreferences';

export function setControllerEditTargetAvoidPreferences(controller) {
  return Core.setKeyValue(KEY__CONTROLLER__EDIT_TARGET_AVOID_PREFERENCES, controller);
}
export function getControllerEditTargetAvoidPreferences() {
  return Obj(Core.getKeyValue(KEY__CONTROLLER__EDIT_TARGET_AVOID_PREFERENCES));
}

export function EditTargetAvoidPreferences({
  type = ENTITY__SCHOOL,
  ...props
}) {
  const [state, updateState] = useState();

  const LIST__TARGET = Arr(state.target);
  const LIST__AVOID = Arr(state.avoid);

  const LIST__SELECTED__TARGET = Arr(state.selectedTarget);
  const LIST__SELECTED__AVOID = Arr(state.selectedAvoid);

  const KEY__TARGET__EXACT_SEARCH = joinKeyName(['target', 'exact']);
  const KEY__AVOID__EXACT_SEARCH = joinKeyName(['avoid', 'exact']);

  const YES__SCHOOL_TYPE = type === ENTITY__SCHOOL;
  const YES__SELECTED__TARGET = YES(LIST__SELECTED__TARGET.length);
  const YES__SELECTED__AVOID = YES(LIST__SELECTED__AVOID.length);
  const YES__TARGET__EXACT_SEARCH = YES(state[KEY__TARGET__EXACT_SEARCH]);
  const YES__AVOID__EXACT_SEARCH = YES(state[KEY__AVOID__EXACT_SEARCH]);

  const NOT__SELECTED__TARGET = NOT(YES__SELECTED__TARGET);
  const NOT__SELECTED__AVOID = NOT(YES__SELECTED__AVOID);
  const NOT__TARGET_FOUND = NOT(Arr(state.target).length);
  const NOT__AVOID_FOUND = NOT(Arr(state.avoid).length);

  const SPACENAME__TYPE = (YES__SCHOOL_TYPE ? 'schools' : 'companies');
  const EXAMPLE__TYPE = (YES__SCHOOL_TYPE ? 'Harvard, Stanford, MIT' : 'Google, Amazon, Facebook');

  const fetchData = async ({
    subtype = 'target',
    search = '',
    ids = [],
  }) => {
    try {
      const where = {};
      let limit = undefined;
      if (subtype.includes('selected')) {
        where.id = { inq: ids };
      }
      else if (search) {
        const YES__EXACT_SEARCH = (
          (subtype === 'target' && YES__TARGET__EXACT_SEARCH) ||
          (subtype === 'avoid' && YES__AVOID__EXACT_SEARCH)
        );
        search = sanitizeArr(
          search.split(',').map(
            (search) => replaceRegExp(search).trim()
          )
        ).map(
          (search) => {
            if (search.length < 3) {
              limit = LIMIT__FETCH_DATA_FOR_UNDEFINED_CASE;
            }
            return YES__EXACT_SEARCH ? `^${search}$` : search;
          }
        ).join('|');
        if (YES__SCHOOL_TYPE) {
          where.universityName = {
            like: search,
            options: 'i'
          };
        }
        else {
          where.name = {
            like: search,
            options: 'i'
          };
        }
      }
      const update = {
        [subtype]: Arr(
          await readLoopbackRecord({
            collection: YES__SCHOOL_TYPE ? COLLECTION__SCHOOL : COLLECTION__COMPANY,
            where,
            order: [
              'name ASC',
              'universityName ASC',
              'crunchbaseOrganizationId DESC',
            ],
            limit: (
              (search.length < 3
                ? LIMIT__FETCH_DATA_FOR_UNDEFINED_CASE
                : limit
              )
            )
          })
        ).map((record) => ({
          ...record,
          LABEL: YES__SCHOOL_TYPE ? record.universityName : record.name
        }))
      };
      await updateState(update);
    }
    catch (error) {
      globalErrorHandler(error);
    }
  };

  setControllerEditTargetAvoidPreferences({
    state,
    updateState,
    applyJobPreferences: async () => {
      const update = {};
      const jobPreferences = Obj(getControllerJobEdit().state?.jobPreferences);
      jobPreferences[SPACENAME__TYPE] = Obj(jobPreferences[SPACENAME__TYPE]);
      jobPreferences[SPACENAME__TYPE].target = LIST__SELECTED__TARGET.map(({ id }) => id);
      jobPreferences[SPACENAME__TYPE].avoid = LIST__SELECTED__AVOID.map(({ id }) => id);
      update.jobPreferences = jobPreferences;
      await getControllerJobEdit().updateState(update);
      getControllerSectionTargetAvoidPreferences().fetchPreferences();
    }
  });

  console.debug(
    'EditTargetAvoidPreferences',
    state
  );

  return (
    <Box column
      {...props}
      className={joinClassName([
        'px-2',
        props.className
      ])}
    // style={{ maxHeight: '75vh' }}
    >
      <Once
        promise={async () => {
          const jobPreferences = Obj(getControllerJobEdit().state?.jobPreferences);
          await fetchData({ subtype: 'target' });
          await fetchData({ subtype: 'avoid' });
          await fetchData({
            subtype: 'selectedTarget',
            ids: jobPreferences[SPACENAME__TYPE]?.target
          });
          await fetchData({
            subtype: 'selectedAvoid',
            ids: jobPreferences[SPACENAME__TYPE]?.avoid
          });
        }}
      />
      <Box row
        className='flex-align-left-top'
      >
        <ColumnPreference
          context={{
            TITLE: 'Target',
            SUB_TYPE: 'target',
            SPACENAME__TYPE,
            EXAMPLE__TYPE,
            LIST__SELECTED: LIST__SELECTED__TARGET,
            LIST: LIST__TARGET,
            NOT__SELECTED: NOT__SELECTED__TARGET,
            NOT__FOUND: NOT__TARGET_FOUND,
            YES__EXACT_SEARCH: YES__TARGET__EXACT_SEARCH,
            KEY__EXACT_SEARCH: KEY__TARGET__EXACT_SEARCH,
            STATE__NAMESPACE: 'selectedTarget',
            fetchData,
            updateState,
            state,
          }}
        />
        <ColumnPreference
          context={{
            TITLE: (
              <span className='c-red'>
                Avoid
              </span>
            ),
            SUB_TYPE: 'avoid',
            SPACENAME__TYPE,
            EXAMPLE__TYPE,
            LIST__SELECTED: LIST__SELECTED__AVOID,
            LIST: LIST__AVOID,
            NOT__SELECTED: NOT__SELECTED__AVOID,
            NOT__FOUND: NOT__AVOID_FOUND,
            YES__EXACT_SEARCH: YES__AVOID__EXACT_SEARCH,
            KEY__EXACT_SEARCH: KEY__AVOID__EXACT_SEARCH,
            STATE__NAMESPACE: 'selectedAvoid',
            fetchData,
            updateState,
            state,
          }}
        />
      </Box>

    </Box>
  );
}

export function ColumnPreference({
  context = {}
}) {
  const mem = useRef({});
  const {
    TITLE,
    SUB_TYPE,
    SPACENAME__TYPE,
    EXAMPLE__TYPE,
    LIST__SELECTED,
    LIST,
    NOT__SELECTED,
    NOT__FOUND,
    YES__FOUND = NOT(NOT__FOUND),
    YES__EXACT_SEARCH = false,
    KEY__EXACT_SEARCH = 'exactSearch',
    STATE__NAMESPACE,
    fetchData,
    updateState,
  } = context;
  const YES__ALL_SELECTED = yesAllElementsIncluded(LIST__SELECTED, LIST);
  return (
    <Fieldset
      title={TITLE}
    >
      <Box column>
        <Box row flex1>
          <Typography className='fw-500 c-black-dark mr-auto'>
            Selected {SPACENAME__TYPE} ({LIST__SELECTED.length})
          </Typography>
          <Button flat small
            className='f-sm'
            onClick={() => updateState({ [STATE__NAMESPACE]: [] })}
          >
            Clear all
          </Button>
        </Box>
        <Box
          className='bg-white-lighter outlined rounded scroll p-1'
          style={{
            height: '18vh'
          }}
        >
          <Message show={NOT__SELECTED}>
            No {SPACENAME__TYPE} selected
          </Message>
          {LIST__SELECTED.map((
            record,
            index,
            array,
          ) => (
            <Chip
              key={joinKeyName([
                KEY__CONTROLLER__EDIT_TARGET_AVOID_PREFERENCES,
                'chip',
                record.id,
                index
              ])}
              className='c-black-medium'
              onDelete={() => {
                updateState({
                  [STATE__NAMESPACE]: removeElements(LIST__SELECTED, [record])
                });
              }}
            >
              {record.LABEL}
            </Chip>
          ))}
        </Box>
      </Box>
      <Box column className='mt-3'>
        <Typography className='fw-500 c-black-dark'>
          Search and select {SPACENAME__TYPE} ({LIST.length})
        </Typography>
        <Typography className='fw-500 c-black-medium f-sm'>
          Can enter multiple {SPACENAME__TYPE} separated by commas. (Example: {EXAMPLE__TYPE})
        </Typography>
        <Box row className='mt-1'>
          <Icon icon='search' className='mr-1' />
          <TextField disabledBlurExport multiLine
            placeholder='Type to search'
            onChange={(event) => {
              clearTimeout(mem.current.timeout);
              mem.current.timeout = setTimeout(() => {
                fetchData({
                  subtype: SUB_TYPE,
                  search: event.target.value
                });
              }, DELAY__FETCH_DATA_AFTER_TYPE);
            }}
            className='mr-1'
          />
          <Checkbox small
            checked={YES__EXACT_SEARCH}
            onChange={(checked) => updateState({
              [KEY__EXACT_SEARCH]: NOT(YES__EXACT_SEARCH)
            })}
            label='Exact'
          />
          <IconButton
            title='Select all verified'
            placement={PLACEMENT__TOP_END}
            icon='playlist_add'
            onClick={() => {
              updateState({
                [STATE__NAMESPACE]: addElements(
                  LIST__SELECTED,
                  LIST.filter(
                    ({ crunchbaseOrganizationId }) => YES(crunchbaseOrganizationId)
                  )
                )
              });
            }}
            disabled={NOT__FOUND}
          />
        </Box>
        <Box
          className='bg-white-lighter outlined rounded scroll py-1 mt-1'
          style={{
            height: '36vh'
          }}
        >
          <Message show={NOT__FOUND}>
            No {SPACENAME__TYPE} found
          </Message>
          {YES__FOUND &&
            <List>
              <ListItem
                key={joinKeyName([
                  KEY__CONTROLLER__EDIT_TARGET_AVOID_PREFERENCES,
                  'option',
                  'select_all',
                ])}
                className='py-0 border-bottom'
              >
                <ListItemButton dense
                  onClick={(event) => {
                    const value = YES__ALL_SELECTED
                      ? removeElements(LIST__SELECTED, LIST)
                      : addElements(LIST__SELECTED, LIST);
                    updateState({ [STATE__NAMESPACE]: value });
                  }}
                >
                  <Typography style={{ width: '20px' }}>
                    &nbsp;
                  </Typography>
                  <ListItemIcon>
                    <Checkbox
                      checked={YES__ALL_SELECTED}
                    />
                  </ListItemIcon>
                  <ListItemText>
                    Select all
                  </ListItemText>
                </ListItemButton>
              </ListItem>
              {LIST.map((
                record,
                index,
                array,
                YES__CHECKED = yesAllElementsIncluded(
                  LIST__SELECTED,
                  [record],
                ),
                YES__CB_ID = YES(record.crunchbaseOrganizationId)
              ) => (
                <ListItem
                  key={joinKeyName([
                    KEY__CONTROLLER__EDIT_TARGET_AVOID_PREFERENCES,
                    TITLE,
                    'option',
                    record.id,
                    index
                  ])}
                  className={joinClassName([
                    'py-0 border-bottom',
                    YES__CB_ID && 'bg-purple-lighter'
                  ])}
                >
                  <ListItemButton dense
                    onClick={(event) => {
                      const value = YES__CHECKED
                        ? removeElements(LIST__SELECTED, [record])
                        : addElements(LIST__SELECTED, [record]);
                      updateState({ [STATE__NAMESPACE]: value });
                    }}
                  >
                    <Typography style={{ width: '20px' }}>
                      {index + 1}
                    </Typography>
                    <ListItemIcon>
                      <Checkbox
                        checked={yesAllElementsIncluded(
                          LIST__SELECTED,
                          [record]
                        )}
                      />
                    </ListItemIcon>
                    <ListItemText>
                      <Box row>
                        {record.LABEL}
                        <Icon
                          acl={YES__CB_ID}
                          icon='verified'
                          className='ml-1 icon16 c-green-dark'
                        />
                      </Box>
                    </ListItemText>
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          }
        </Box>
      </Box>
    </Fieldset>
  );
}

export default async function openDialogTargetAvoidPreferences({
  title = 'Target/Avoid preferences',
  type = ENTITY__SCHOOL
  ,
}) {


  return Core.dialog.open({
    title,
    content: (
      <EditTargetAvoidPreferences
        type={type}
      />
    ),
    actions: [
      <Button outlined minW120
        label="Cancel"
      />,
      <Button primary minW120
        label="Apply"
        onClick={
          (event) => getControllerEditTargetAvoidPreferences().applyJobPreferences()
        }
      />,
    ],
    paperStyle: {
      width: 1600,
    }
  });
}
